import axios from 'axios';
import type { EnumAppMode } from '@getaccept/lib-shared/src/enums/AppMode';
import type { DashboardWidgetsDTO } from '../types/dashboard-widgets-dto';
import type { DashboardActivity } from '../../../dashboard/types/dashboard-activity';
import type { DashboardWidgets } from '../../../dashboard/types/dashboard-widgets';
import type { DashboardExpiringDocument } from '../../../dashboard/types/dashboard-expiring-document';

export class DashboardService {
  static async getActivity({ showAll }: { showAll: boolean }): Promise<DashboardActivity[]> {
    const body = new FormData();
    body.append('showall', `${showAll}`);

    const { data } = await axios.post('/get/board/activity', body);

    if (!data?.activity) {
      return [];
    }
    return data.activity.map(activityItem => ({
      createdAt: activityItem.created_at,
      documentId: activityItem.document_id,
      documentName: activityItem.document_name,
      documentStatus: activityItem.document_status,
      eventMetadata: activityItem.event_meta_data,
      eventName: activityItem.event_name,
      firstName: activityItem.first_name,
      lastName: activityItem.last_name,
      email: activityItem.email,
      mobile: activityItem.mobile,
      phone: activityItem.phone,
      thumbUrl: activityItem.thumb_url,
      companyName: activityItem.company_name,
    }));
  }

  static async getExpiringSoon({
    showAll,
  }: {
    showAll: boolean;
  }): Promise<DashboardExpiringDocument[]> {
    const body = new FormData();
    body.append('showall', `${showAll}`);
    body.append('filter', 'expiring');
    body.append('limit', '10');
    const {
      data: { documents },
    } = await axios.post('/get/document/list', body);
    return documents
      .filter(document => !!document.expiration_date)
      .map(document => ({
        expirationDate: document.expiration_date,
        name: document.name,
        id: document.id,
        type: document.type,
      }));
  }

  static async getWidgets({
    showAll,
    appMode,
  }: {
    showAll: boolean;
    appMode: EnumAppMode;
  }): Promise<DashboardWidgets> {
    const body = new FormData();
    body.append('showall', `${showAll}`);
    body.append('app-mode', `${appMode}`);

    const {
      data: { widget },
    }: { data: { widget: DashboardWidgetsDTO } } = await axios.post('/get/board/widgets', body);

    if (!widget) {
      throw new Error('could not load widgets');
    }
    return {
      active: widget.active,
      dealtime: {
        countDays: widget.dealtime.count_days,
        countHours: widget.dealtime.count_hours,
      },
      dealtimeLast: {
        countDays: widget.dealtime_last.count_days,
        countHours: widget.dealtime_last.count_hours,
      },
      estimated: widget.estimated,
      expired: widget.expired,
      hit: widget.hit,
      hitLast: widget.hit_last,
      hitTotal: widget.hit_total,
      hot: widget.hot,
      sent: widget.sent,
      signed: widget.signed,
      valleyOfDeath: widget.vod,
      won: widget.won,
      wonLast: widget.won_last,
      performers: widget.performers
        ? widget.performers.map(performer => ({
            firstName: performer.first_name,
            lastName: performer.last_name,
            thumbUrl: performer.thumb_url,
            count: performer.count,
            value: performer.value,
            total: performer.total,
          }))
        : [],
    };
  }
}
